import { cx } from 'emotion';
import { number, bool, func } from 'prop-types';
import { applySpec, clamp, values } from 'ramda';
import React, {
  Fragment, useMemo, useRef, forwardRef,
} from 'react';

import { useAppState } from '@ezugi/bootstrap/src/hooks/useAppState';
import { useIntl } from '@ezugi/bootstrap/src/hooks/useIntl';
import statsIcon from '@ezugi/static/icons/stats.svg';
import { DeviceInfo } from '@ezugi/utils';

import { gameHistorySelector } from '../../store/selectors/game';
import Stack from './Stack';
import ToggleSelect from './ToggleSelect/ToggleSelect';
import {
  StacksContainer,
  toggleSelectClassName,
  mobileClassName,
  verticalClassName,
} from './style';

const spec = applySpec({
  gameHistory: gameHistorySelector,
});

const INCREMENTS = [ 50, 100, 250, 500 ];
const intlKeys = { lastRounds: 'sicbo_ui.last_rounds' };

const getListIntlKeys = (gameResults) => {
  const count = gameResults.length;
  let capped = false;

  return INCREMENTS.reduce(
    (acc, x, i) => {
      if (!capped) {
        const value = clamp(0, count, x);
        acc.keys[x] = `sicbo_ui.last_${x}_rounds`;
        acc.sizes[i] = value;

        capped = x >= count;
        if (capped) {
          acc.variables[x] = value;
        }
      }

      return acc;
    },
    { keys: intlKeys, variables: {}, sizes: {} }
  );
};

// The total size (including margins) a result should have
const ROUND_SIZE = 34;
const ROUND_SIZE_VERTICAL = 70;

const LastRounds = forwardRef(
  (
    {
      rounds = Infinity, // We need a max number of rounds to use together with `constrain` prop
      showDices,
      constrain,
      showRoundsSelector,
      className,
      pageIndex,
      setPageIndex,
      flatView,
      ...rest
    },
    ref
  ) => {
    const [ { gameHistory } ] = useAppState(spec);
    const {
      keys: intlListKeys,
      variables,
      sizes,
    } = useMemo(
      () => getListIntlKeys(
        gameHistory
      ),
      [ gameHistory ]
    );
    const locales = useIntl({ keys: intlListKeys, variables });

    const localizedList = values(locales);
    const stacksRef = useRef(null);
    let max = rounds;

    // Limit the number of round results based on the available space
    if (constrain && stacksRef.current) {
      const { parentNode } = stacksRef.current;

      const dimension = stacksRef.current.clientHeight > stacksRef.current.clientWidth
        ? parentNode.clientHeight
        : parentNode.clientWidth;

      max = Math.floor(dimension / flatView ? ROUND_SIZE_VERTICAL : ROUND_SIZE);
    }

    const stackContainerColumns = !constrain && !showRoundsSelector && isFinite(rounds) ? rounds : 8;
    const nrOfRounds = showRoundsSelector
      ? sizes[pageIndex]
      : Math.min(max, rounds);
    const roundsChunk = gameHistory.slice(0, nrOfRounds);

    return (
      <Fragment>
        {showRoundsSelector && (
          <ToggleSelect
            list={localizedList}
            defaultActive={0}
            onToggle={setPageIndex}
            className={toggleSelectClassName}
            iconSvg={statsIcon}
            title={locales.lastRounds}
            ref={ref}
          />
        )}
        <StacksContainer
          columns={stackContainerColumns}
          ref={stacksRef}
          dir="ltr"
          className={cx(className, {
            [verticalClassName]: flatView,
            [mobileClassName]: DeviceInfo.isHandheld,
          })}
          {...rest}
        >
          {roundsChunk.map((stack, index) => (
            <Stack
              key={stack.roundId || index}
              {...stack}
              showDices={showDices}
            />
          ))}
        </StacksContainer>
      </Fragment>
    );
  }
);

LastRounds.propTypes = {
  rounds: number,
  showDices: bool,
  showRoundsSelector: bool,
  flatView: bool,
  constrain: bool,
  pageIndex: number,
  setPageIndex: func,
};

LastRounds.defaultProps = {
  flatView: false,
  showDices: true,
  showRoundsSelector: false,
  constrain: false,
  pageIndex: 0,
  setPageIndex: () => undefined,
};

export default LastRounds;
